import {
  ArchiveBoxIcon,
  ArrowUturnLeftIcon,
} from "@heroicons/react/24/outline";
import { toast } from "sonner";
import { useTranslations } from "use-intl";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Spinner } from "@/lib/Components/Layout/Loaders/Spinner";
import { useSetThreadClosed } from "@/app/Threads/hooks/useSetThreadClosed";
import { useThreadSelection } from "@/app/Threads/hooks/useThreadSelection";
import { ThreadActionTarget } from "@/app/Threads/hooks/useThreadActions";

/**
 * Fades in on row hover. It sits in the gutter the row reserves for it, so it
 * never covers what the row is showing.
 *
 * While the thread has a change in flight the gutter holds a spinner instead,
 * shown whether or not the row is hovered and whether or not the change was
 * asked for here — the row is waiting on the server either way.
 */
export function ThreadQuickCloseButton({
  thread,
  isPending,
}: {
  thread: ThreadActionTarget;
  isPending: boolean;
}) {
  const t = useTranslations("thread.actions");
  const { mutate: setClosed } = useSetThreadClosed();
  const { selectNextAfter } = useThreadSelection();

  const label = thread.closed ? t("reopen") : t("close");
  const Icon = thread.closed ? ArrowUturnLeftIcon : ArchiveBoxIcon;

  if (isPending) {
    return (
      <span className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-gray-400">
        <Spinner className="size-4" />
      </span>
    );
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={label}
          onClick={(event) => {
            event.stopPropagation();
            selectNextAfter(thread.id);
            setClosed(
              { id: thread.id, closed: !thread.closed },
              { onError: () => toast.error(t("setClosedFailed")) },
            );
          }}
          className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1.5 text-gray-400 opacity-0 transition-opacity hover:bg-gray-200 hover:text-gray-700 focus-visible:pointer-events-auto focus-visible:opacity-100 group-hover:pointer-events-auto group-hover:opacity-100"
        >
          <Icon className="size-4" />
        </button>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}
